// Components
import React, {PureComponent, ChangeEvent, FormEvent} from 'react'
import {Honeybadger} from '@honeybadger-io/react'
import {
  Panel,
  Method,
  Heading,
  ComponentSize,
  JustifyContent,
  HeadingElement,
  ComponentStatus,
  Notification,
  Gradients,
} from '@influxdata/clockface'
import auth0js from 'auth0-js'

import CSRF from 'js/components/CSRF'
import {Auth0Connection} from 'js/types'
import SignUpForm from './SignUpForm'
import SocialSignupButtonsHeader from '../shared/SocialSignupButtonsHeader'
import SocialFocusedSignup, {SocialFocus} from './SocialFocusedSignup'

const {auth0: auth0Config} = (window as any).Config

const auth0 = new auth0js.WebAuth({
  domain: auth0Config.domain,
  clientID: auth0Config.clientID,
  redirectUri: auth0Config.signupRedirectUri,
  responseType: 'code',
})

export interface SignupError {
  type: 'existingUser' | 'generic'
  additional?: string
}

interface Props {
  socialFocus?: SocialFocus
  signupError?: SignupError
  existingUser?: string
  marketplaceName: string
}

interface State {
  isSocialFocused: boolean
  buttonStatus: ComponentStatus
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  firstNameError: string
  lastNameError: string
  emailError: string
  passwordError: string
  confirmPasswordError: string
  showErrorNotification: boolean
}

class SignUpPageContents extends PureComponent<Props> {
  state: State = {
    isSocialFocused: !!this.props.socialFocus && !this.props.signupError,
    buttonStatus: ComponentStatus.Default,
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstNameError: '',
    lastNameError: '',
    emailError: '',
    passwordError: '',
    confirmPasswordError: '',
    showErrorNotification: false,
  }

  componentDidMount() {
    const {signupError} = this.props
    if (signupError) {
      const email =
        signupError.type === 'existingUser' ? signupError.additional : ''
      this.setState({
        showErrorNotification: true,
        isSocialFocused: false,
        email: email,
      })
      if (signupError.type === 'existingUser') {
        this.displayErrorMessage({}, {code: 'user_exists'})
      }
    }
  }

  render() {
    const {
      buttonStatus,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      firstNameError,
      lastNameError,
      emailError,
      passwordError,
      confirmPasswordError,
      isSocialFocused,
      showErrorNotification,
    } = this.state

    return (
      <form
        action="/signup"
        method={Method.Post}
        onSubmit={this.handleSubmit}
        className="sign-up--form"
      >
        {showErrorNotification && (
          <Notification
            size={ComponentSize.Medium}
            gradient={Gradients.JustPeachy}
            style={{width: '100%'}}
            onDismiss={this.handleDismissNotification}
            testID="errorNotification"
          >
            {this.notificationMessage}
          </Notification>
        )}
        {isSocialFocused ? (
          <SocialFocusedSignup
            handleSocialClick={this.handleSocialClick}
            handleChooseEmail={this.handleChooseEmail}
            focus={this.props.socialFocus}
          />
        ) : (
          <Panel className="sign-up--form-panel sign-up--marketplace-panel">
            <Panel.Header
              size={ComponentSize.Large}
              justifyContent={JustifyContent.Center}
              className="sign-up--form-header"
            >
              <Heading element={HeadingElement.H3}>Continue with</Heading>
            </Panel.Header>
            <Panel.Body size={ComponentSize.Large}>
              <SocialSignupButtonsHeader
                handleSocialClick={this.handleSocialClick}
              />
              <Heading
                element={HeadingElement.H3}
                className="sign-up--form-separator"
              >
                OR
              </Heading>
              <SignUpForm
                buttonStatus={buttonStatus}
                confirmPassword={confirmPassword}
                confirmPasswordError={confirmPasswordError}
                email={email}
                emailError={emailError}
                firstName={firstName}
                firstNameError={firstNameError}
                handleInputChange={this.handleInputChange}
                lastName={lastName}
                lastNameError={lastNameError}
                password={password}
                passwordError={passwordError}
              />
              <CSRF />
            </Panel.Body>
          </Panel>
        )}
      </form>
    )
  }

  private get notificationMessage(): string {
    const {marketplaceName, signupError} = this.props

    if (signupError) {
      switch (signupError.type) {
        case 'existingUser':
          return `Currently we do not support using an existing InfluxDB Cloud account to sign up for ${marketplaceName} Marketplace. Please select a different email address.`
        case 'generic':
          return `We have been notified of an issue while creating an InfluxDB Cloud account associated with your ${marketplaceName} Marketplace account. If this issue persists, please contact support@influxdata.com`
      }
    }
  }

  private handleChooseEmail = () => {
    this.setState({isSocialFocused: false})
  }

  private get validFieldValues(): {
    isValid: boolean
    errors: {[fieldName: string]: string}
  } {
    const {firstName, lastName, email, password, confirmPassword} = this.state

    const firstNameError = !firstName && 'This field is required'
    const lastNameError = !lastName && 'This field is required'
    const emailError = !email && 'This field is required'
    const passwordError = !password && 'This field is required'
    const confirmPasswordError =
      confirmPassword === password
        ? !confirmPassword && 'This field is required'
        : 'Passwords must match'

    const errors = {
      firstNameError,
      lastNameError,
      emailError,
      passwordError,
      confirmPasswordError,
    }

    const isValid = !Object.values(errors).some((error) => !!error)

    return {isValid, errors}
  }

  private handleSubmit = (e: FormEvent) => {
    const {isValid, errors} = this.validFieldValues
    const {
      email,
      password,
      firstName: given_name,
      lastName: family_name,
    } = this.state

    e.preventDefault()

    if (!isValid) {
      this.setState(errors)
      return
    }

    this.setState({buttonStatus: ComponentStatus.Loading})

    auth0.signup(
      {
        connection: Auth0Connection.Authentication,
        email,
        password,
        family_name,
        given_name,
      } as any,
      (err) => {
        if (err) {
          this.displayErrorMessage(errors, err)
          this.setState({
            buttonStatus: ComponentStatus.Default,
            showErrorNotification: true,
          })
          return
        }
        auth0.login(
          {
            realm: Auth0Connection.Authentication,
            email,
            password,
          },
          () => {}
        )
      }
    )
  }

  private displayErrorMessage = (errors, auth0Err) => {
    if (/error in email/.test(auth0Err.code)) {
      this.setState({
        ...errors,
        emailError: 'Please enter a valid email address',
      })
    } else if (
      auth0Err.code === 'user_exists' ||
      auth0Err.code === 'extensibility_error'
    ) {
      const emailError =
        'This email address is already used for an account. Please use a different email address.'
      this.setState({...errors, emailError})
    } else {
      const emailError =
        'We have been notified of an issue while creating your account. If this issue persists, please contact support@influxdata.com'
      this.setState({...errors, emailError})
      Honeybadger.notify(auth0Err.description || auth0Err.code, auth0Err.name)

      throw new Error(auth0Err.description)
    }
  }

  private handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({[e.target.name]: e.target.value})
  }

  private handleSocialClick = (connection: Auth0Connection) => {
    auth0.authorize({
      redirectUri: auth0Config.marketplaceSignupRedirectUri,
      connection,
    })
  }

  private handleDismissNotification = () => {
    this.setState({showErrorNotification: false})
  }
}

export default SignUpPageContents
